import React, { SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import { captureException, withScope } from '@sentry/browser';
import classNames from 'classnames';
import { loader } from 'graphql.macro';
import { first, get, isNumber } from 'lodash';
import moment, { Moment } from 'moment-timezone';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import DialogContent from '@material-ui/core/DialogContent';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ApolloClient from 'apollo-client';
import { MockApolloClient } from 'mock-apollo-client';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';

import { CoachingVisitState, ServiceType } from 'src/generated/webapp_gql';
import { ProviderType } from 'src/constants';

import { Dispatch } from '../../config/Store';
import { getCurrentUser } from '../../selectors/userSelectors';
import { getUserProviderType } from '../../rematch-models/user';
import { colors } from '../../themes/colors';
import { textInputStyle } from '../../themes/input';
import { captureSentryException } from '../../helpers/sentryHelper';
import { CoachAssignment } from '../../models/Common';
import {
  CoachingVisit,
  FormattedCoachingVisit,
  ReportedCoachingVisit,
} from '../../models/Sessions';
import { UserRole } from '../../models/User';
import MHButton from '../MHButton';
import MHDatePicker from '../MHDatePicker';
import MHRadio from '../MHRadio';
import { QuestionBubble } from '../Clients/QuestionBubble';
import { PopoverPosition } from '../Clients/Popover';
import { ClientSearchSelection } from './ClientSearchSelection';
import { GroupPracticeClientSearchSelection } from './GroupPracticeClientSearchSelection';
import { InvoiceSubmissionSummaryModal } from './InvoiceSubmissionSummaryModal';
import { ProviderSearchSelection } from './ProviderSearchSelection';
import { BillingCodesForm } from './BillingCodesForm';
import { RateSessionForm } from './RateSessionForm';
import ReportedCoachingVisitSelector from './ReportedCoachingVisitSelector/ReportedCoachingVisitSelector';
import { CoachingVisitToReportQuery } from './documents/CoachingVisitToReport.generated';
import { VisitType } from '../Sessions/SessionsList/SessionsByDay/SessionRow/SessionType/VisitTypeChip/constants';
import usePrevious from '../../hooks/usePrevious';

const COACH_ASSIGNMENT_QUERY = loader('./documents/CoachAssignmentQuery.gql');
const REPORT_EXISTING_VISIT_MUTATION = loader('./documents/ReportExistingVisitMutation.gql');
const CREATE_OR_UPDATE_NEW_PROVIDER_REVIEW_FOR_REPORT_VISIT_MUTATION = loader(
  './documents/CreateOrUpdateNewProviderReviewForReportVisitMutation.gql',
);
const CREATE_NEW_VISIT_FOR_REPORT_VISIT_MUTATION = loader(
  './documents/CreateNewVisitForReportVisitMutation.gql',
);
const GET_COACHING_VISIT_CLAIMABLE = loader(
  'src/components/Invoicing/documents/GetCoachingVisitClaimable.gql',
);

type RadioButtonOptionData = {
  label: string;
  value: string | boolean;
  hidden?: boolean;
};

interface CoachAssignmentQuery {
  coachAssignments: CoachAssignment[];
}

interface CoachAssignmentQueryVars {
  clientUuid: string;
  providerUuid: string;
}

interface CoachVisitClaimableQueryParams {
  memberUuid: string;
  state: string;
  scheduledTime: Date;
}

interface CoachVisitClaimableQueryResponse {
  coachingVisitClaimable: {
    claimable: boolean;
  };
}

export enum InvoicingFormTestID {
  ProviderField = 'InvoicingFormTestID.ProviderField',
  ClientField = 'client-field',
  ClientSessionsInfo = 'client-sessions-info',
  MHDatePicker = 'InvoicingForm.MHDatePickerTestID',
}

export enum InvoicingValidationErrorCode {
  OverlappingVisitMember = 'OVERLAPPING_VISIT_MEMBER',
  OverlappingVisitProvider = 'OVERLAPPING_VISIT_PROVIDER',
  NoVisitsRemaining = 'NO_VISITS_REMAINING',
}

const styles = (theme: Theme) =>
  createStyles({
    ...textInputStyle(theme),
    dialogContent: {
      overflowY: 'initial',
      marginTop: 0,
      padding: 0,
      paddingBottom: 64,
    },
    dialogContentInner: {
      display: 'flex',
      justifyContent: 'center',
    },
    dialogContentLeft: {
      flex: '1 1 0',
      minWidth: 0,
      borderRight: `1.3px solid ${colors.blue7}`,
    },
    dialogContentRight: {
      flex: '1 1 0',
      minWidth: 0,
    },
    visitRow: {
      margin: '0px 36px 15px',
    },
    formLabel: {
      fontFamily: 'Avenir-Heavy',
      fontSize: 14,
      color: colors.black3,
    },
    formLabelStar: {
      color: colors.red2,
      fontSize: 18,
    },
    radioOption: {
      fontFamily: 'Avenir-Medium',
      marginTop: 3,
      fontSize: 14,
      color: colors.black2,
    },
    selectedRadioOption: {
      fontFamily: 'Avenir-Heavy',
      marginTop: 3,
      fontWeight: 800,
      color: colors.black5,
    },
    radioContainer: {
      display: 'flex',
      flexDirection: 'column',
    },
    radioBox: {
      display: 'flex',
      marginLeft: -9,
      flexDirection: 'row',
      alignItems: 'center',
    },
    submitButtonSize: {
      flex: '1 1 auto',
      minWidth: 0,
    },
    buttonsContainer: {
      position: 'fixed',
      backgroundColor: colors.white1,
      paddingTop: 12,
      paddingBottom: 16,
      bottom: 0,
      left: 0,
      right: 0,
      display: 'flex',
      justifyContent: 'center',
      zIndex: 50, // this handles the 25 z-index disabledCover
    },
    buttonsWrapper: {
      width: '40%',
      display: 'flex',
      gap: '12px',
    },
  });

export enum AttendanceOptionValue {
  Completed = 'COMPLETED',
  LateCancellation = 'LATE_CANCELLATION',
  NoShow = 'NO_SHOW',
}

export enum AttendanceOptionLabel {
  Completed = 'Attended',
  LateCancellation = 'Late Cancellation',
  NoShow = 'No show',
}

export enum ModalityOptionValue {
  InPerson = 'IN_PERSON',
  Video = 'VIDEO',
  Phone = 'PHONE',
}

export enum ModalityOptionLabel {
  InPerson = 'In-person',
  Video = 'Video',
  Phone = 'Phone',
}

export enum SessionTypeOptionLabel {
  IndividualSession = 'Individual Session',
  CouplesSession = 'Group Session',
}

const modalityOptions: RadioButtonOptionData[] = [
  { value: ModalityOptionValue.InPerson, label: ModalityOptionLabel.InPerson },
  { value: ModalityOptionValue.Video, label: ModalityOptionLabel.Video },
  { value: ModalityOptionValue.Phone, label: ModalityOptionLabel.Phone },
];

const sessionTypeOptions: RadioButtonOptionData[] = [
  { value: false, label: SessionTypeOptionLabel.IndividualSession },
  { value: true, label: SessionTypeOptionLabel.CouplesSession },
];

export interface InvoicingFormProps {
  canSelectReportedCoachingVisits?: boolean;
  hideSuccessSnackbar?: boolean;
  inModal?: boolean;
  isProviderInvoicingOnly: boolean;
  memberClient: ApolloClient<NormalizedCacheObject> | MockApolloClient;
  onCancel?: () => void;
  onSessionDateChange?: (date: Moment) => void;
  onSuccess?: () => void;
  onUpdateVisit?: () => void;
  setError?: (errorMessage: string) => void;
  showSummaryModal?: boolean;
  specificCoachAssignment?: CoachAssignment;
  specificVisit?: CoachingVisit | FormattedCoachingVisit;
}

type Props = WithStyles & InvoicingFormProps;

type VisitTypeInterface =
  | CoachingVisit
  | FormattedCoachingVisit
  | ReportedCoachingVisit
  | undefined;

const InvoicingForm: React.FC<Props> = ({
  canSelectReportedCoachingVisits = false,
  classes,
  hideSuccessSnackbar,
  inModal,
  isProviderInvoicingOnly,
  memberClient,
  onCancel,
  onSessionDateChange,
  onSuccess,
  onUpdateVisit,
  setError,
  showSummaryModal,
  specificCoachAssignment,
  specificVisit,
}) => {
  const {
    alwaysShowBillingStepForValidation,
    showProviderBillingStep,
    hpiSessionInvoice,
    invoicingDashboard,
  } = useFlags();
  const dispatch: Dispatch = useDispatch();
  const providerType = useSelector(getUserProviderType);
  const serviceType =
    providerType === ProviderType.PSYCHIATRIST ? ServiceType.Psychiatry : ServiceType.Therapy;
  const user = useSelector(getCurrentUser);

  const { enqueueSnackbar } = useSnackbar();

  const [visit, setVisit] = useState<VisitTypeInterface>(specificVisit);

  const [sessionDateTime, setSessionDateTime] = useState(
    specificVisit && specificVisit.scheduledTime
      ? moment.tz(moment(specificVisit.scheduledTime).toISOString(), user.localTimezone)
      : null,
  );
  // Attendance option
  const [state, setState] = useState(specificVisit ? specificVisit.state : '');
  const [modalityType, setModalityType] = useState(
    specificVisit && specificVisit.modalityType ? specificVisit.modalityType : '',
  );
  const [isCoupleSession, setIsCoupleSession] = useState(
    specificVisit && specificVisit.isCoupleSession ? specificVisit.isCoupleSession : false,
  );
  const [organizationImprovements, setOrganizationImprovements] = useState(
    specificVisit?.previousProviderReview?.feedbackText ||
      specificVisit?.organizationImprovements ||
      '',
  );

  const shouldRequireReportedVisit = !isProviderInvoicingOnly && hpiSessionInvoice;

  const attendanceOptions: RadioButtonOptionData[] = [
    {
      value: AttendanceOptionValue.Completed,
      label: AttendanceOptionLabel.Completed,
      hidden:
        visit?.visitType === VisitType.HealthPlan &&
        (visit.state === CoachingVisitState.LateCancellation ||
          visit.state === CoachingVisitState.Cancelled),
    },
    {
      value: AttendanceOptionValue.LateCancellation,
      label: AttendanceOptionLabel.LateCancellation,
    },
    {
      value: AttendanceOptionValue.NoShow,
      label: AttendanceOptionLabel.NoShow,
    },
  ];

  useEffect(() => {
    if (!shouldRequireReportedVisit) return;

    setVisit(specificVisit);
  }, [setVisit, shouldRequireReportedVisit, specificVisit]);

  const previousVisit = usePrevious(visit);

  useEffect(() => {
    if (!shouldRequireReportedVisit) return;

    if (
      (!previousVisit && visit) ||
      (visit && previousVisit && previousVisit.uuid !== visit.uuid)
    ) {
      setSessionDateTime(moment.tz(moment(visit.scheduledTime).toISOString(), user.localTimezone));
      setState(visit.state);
      setModalityType(visit.modalityType || '');
      setIsCoupleSession(visit.isCoupleSession || false);
      setOrganizationImprovements(
        visit.previousProviderReview?.feedbackText || visit.organizationImprovements || '',
      );
    }
  }, [
    previousVisit,
    setIsCoupleSession,
    setModalityType,
    setOrganizationImprovements,
    setSessionDateTime,
    setState,
    shouldRequireReportedVisit,
    user,
    visit,
  ]);

  // Assumption: all group practices are therapy
  const isGroupPractice = user.role === UserRole.GroupPractice;
  const isTherapy = isGroupPractice || providerType === 'Therapist';
  const defaultDuration = isGroupPractice || providerType === 'Therapist' ? 60 : 30;
  const scheduledDuration = visit?.scheduledDuration || defaultDuration;
  const actualDuration = visit?.actualDuration || defaultDuration;

  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoiceSubmissionSummaryModalVisible, setInvoiceSubmissionSummaryModalVisible] =
    useState(false);
  const [groupPracticeProviderUuid, setGroupPracticeProviderUuid] = useState('');
  const [coachAssignment, setCoachAssignment] = useState(specificCoachAssignment);
  const [visitsRemaining, setVisitsRemaining] = useState<{
    visitsRemaining: number;
    coveredVisitsTotal: number;
    resetDate: string;
  } | null>(null);

  const [abilityToHelpClient, setAbilityToHelpClient] = useState(
    visit?.previousProviderReview?.abilityToHelpClient || 0,
  );
  const [workedOnAgreedUponGoals, setWorkedOnAgreedUponGoals] = useState(
    visit?.previousProviderReview?.workedOnAgreedUponGoals || 0,
  );
  const [clearPlanToWorkTowardGoals, setClearPlanToWorkTowardGoals] = useState(
    visit?.previousProviderReview?.clearPlanToWorkTowardGoals || 0,
  );

  const [cptCode, setCptCode] = useState('');
  const [primaryDiagnosisCode, setPrimaryDiagnosisCode] = useState('');
  const [secondaryDiagnosisCodes, setSecondaryDiagnosisCodes] = useState<string[]>([]);

  const formattedSessionTime = sessionDateTime?.format('MMMM Do [at] h:mm a');
  const memberName = `${coachAssignment?.member.firstName} ${coachAssignment?.member.lastName}`;

  const InvoicingValidationErrorMessages = {
    [InvoicingValidationErrorCode.OverlappingVisitProvider]: `Oops! You already have a session recorded at this time.`,
    [InvoicingValidationErrorCode.OverlappingVisitMember]: `Oops! We already have a session recorded on ${formattedSessionTime} for ${memberName}.`,
    [InvoicingValidationErrorCode.NoVisitsRemaining]: `Oops, ${memberName} does not have anymore covered ${
      isTherapy ? 'therapy' : 'coaching'
    } sessions left on Modern Health.`,
  };

  const [isCoachingVisitClaimable, setIsCoachingVisitClaimable] = useState(false);
  const [coachingVisitClaimable, { loading: coachingVisitClaimableLoading }] = useLazyQuery<
    CoachVisitClaimableQueryResponse,
    CoachVisitClaimableQueryParams
  >(GET_COACHING_VISIT_CLAIMABLE, {
    client: memberClient,
    fetchPolicy: 'network-only',
    onCompleted: ({ coachingVisitClaimable: { claimable } }) => {
      setIsCoachingVisitClaimable(claimable);
    },
    onError: captureException,
  });

  // When a client is selected via dropdown, search for the corresponding coach assignment.
  const [getCoachAssignment, { variables, loading: getCoachAssignmentLoading }] = useLazyQuery<
    CoachAssignmentQuery,
    CoachAssignmentQueryVars
  >(COACH_ASSIGNMENT_QUERY, {
    client: memberClient,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const assignment = first(data?.coachAssignments);

      if (!assignment) {
        withScope((scope) => {
          scope.setExtra('client_uuid', variables.clientUuid);
          scope.setExtra('provider_uuid', variables.providerUuid);

          captureException(
            new Error("Couldn't find coach assignment for a client with given uuid"),
          );
        });

        return;
      }

      setCoachAssignment(assignment);

      const company = assignment.member?.company;

      const coveredVisits = get(
        company,
        isTherapy ? 'coveredTherapyVisits' : 'coveredCoachingVisits',
      );
      const remainingVisits = get(
        assignment.member?.visitsRemaining,
        isTherapy ? 'therapyVisitsRemaining' : 'coachingVisitsRemaining',
      );

      // This shouldn't happen, but the types in GraphQL don't guarantee member or company fields as non-nullable
      if (!company?.resetDate || !isNumber(coveredVisits) || !isNumber(remainingVisits)) {
        return setVisitsRemaining(null);
      }

      setVisitsRemaining({
        visitsRemaining: remainingVisits,
        coveredVisitsTotal: coveredVisits,
        resetDate: company.resetDate,
      });
    },
    onError: captureException,
  });

  const handleProviderSelect = (providerUuid: string) => {
    setGroupPracticeProviderUuid(providerUuid);
  };

  const handleClientSelect = useCallback(
    (clientUuid: string) => {
      getCoachAssignment({
        variables: { clientUuid, providerUuid: groupPracticeProviderUuid || user.uuid },
      });
    },
    [getCoachAssignment, groupPracticeProviderUuid, user],
  );

  const handleSubmit = async () => {
    setSubmitted(true);

    if (isFormInvalid) {
      return;
    }

    if (showSummaryModal && !invoiceSubmissionSummaryModalVisible) {
      setInvoiceSubmissionSummaryModalVisible(true);
    } else {
      await saveCoachVisit();

      setInvoiceSubmissionSummaryModalVisible(false);
    }
  };

  const handleCptCodeSelected = (code: string) => setCptCode(code);
  const handlePrimaryDiagnosisCodeSelected = (code: string) => setPrimaryDiagnosisCode(code);
  const handleSecondaryDiagnosisCodesSelected = (codes: string[]) =>
    setSecondaryDiagnosisCodes(codes);

  const saveCoachVisit = async () => {
    try {
      if (!coachAssignment) {
        const error = new Error(
          'Attempted to submit invoice without corresponding coach assignment.',
        );
        captureSentryException(error);
        throw error;
      }

      if ((shouldRequireReportedVisit && !visit) || !sessionDateTime || !state || !modalityType) {
        return;
      }

      setSaving(true);

      // CC-1893: Bug where sessions overlap because of seconds in scheduled time
      // Set seconds and milliseconds to 0
      const formattedSessionDateTime = moment(sessionDateTime).seconds(0).milliseconds(0);

      const visitData: Omit<CoachingVisit, 'coachAssignment'> = {
        scheduledDuration,
        actualDuration,
        scheduledTime: formattedSessionDateTime,
        isTherapy,
        isCoupleSession,
        organizationImprovements,
        state,
        modalityType,
        primaryDiagnosisCode,
        secondaryDiagnosisCodes,
        cptCode,
      };

      const providerReviewData = {
        abilityToHelpClient,
        workedOnAgreedUponGoals,
        clearPlanToWorkTowardGoals,
        feedbackText: organizationImprovements,
      };

      if (visit) {
        await memberClient.mutate({
          mutation: REPORT_EXISTING_VISIT_MUTATION,
          variables: {
            coachingVisitUuid: visit.uuid,
            ...providerReviewData,
            ...visitData,
          },
        });
      } else {
        const response = await memberClient.mutate({
          mutation: CREATE_NEW_VISIT_FOR_REPORT_VISIT_MUTATION,
          variables: {
            coachAssignmentUuid: coachAssignment.uuid,
            ...visitData,
          },
        });

        await memberClient.mutate({
          mutation: CREATE_OR_UPDATE_NEW_PROVIDER_REVIEW_FOR_REPORT_VISIT_MUTATION,
          variables: {
            coachingVisitUuid: response.data.createVisit.coachingVisitUuid,
            ...providerReviewData,
          },
        });
      }

      // When we update visits we need to refetch everything because the back-end calculates visits remaining.
      await dispatch.partners.fetchPartners();

      setSaving(false);

      if (!hideSuccessSnackbar) {
        enqueueSnackbar('Save success!', { variant: 'success' });
      }

      if (onUpdateVisit) {
        onUpdateVisit();
      }
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      setSaving(false);

      const errorCode = Object.values(InvoicingValidationErrorCode).find((errorCode) =>
        error.message.includes(errorCode),
      );

      if (
        error.message.includes(
          'Can only report completed or no show visits after visit has started',
        ) &&
        setError
      ) {
        setError(
          `You can only report ${state
            .toLowerCase()
            .replace('_', ' ')} visits after a session has been completed.`,
        );
      } else if (
        error.message.includes(
          'A late cancellation visit already exists for this assignment and scheduled time',
        ) &&
        setError
      ) {
        setError(`A late cancellation visit already exists for this member and scheduled time`);
      } else if (errorCode && setError) {
        setError(InvoicingValidationErrorMessages[errorCode]);
      } else {
        enqueueSnackbar('Something went wrong saving info.', { variant: 'error' });
        captureException(error);
      }
    }
  };

  const renderRadioButtons = (
    data: RadioButtonOptionData[],
    selectedOption: string | boolean,
    onChange: (value: SetStateAction<any>) => void,
    errorMessage: string,
  ) => {
    const anyOptionSelected = data.some(({ value }) => value === selectedOption);

    return (
      <div className={classes.radioContainer}>
        {data.map(
          ({ label, value, hidden }) =>
            !hidden && (
              <div className={classes.radioBox} key={label}>
                <MHRadio
                  checked={selectedOption === value}
                  onChange={() => onChange(value)}
                  color="primary"
                />
                <Typography
                  className={
                    selectedOption === value ? classes.selectedRadioOption : classes.radioOption
                  }
                >
                  {label}
                </Typography>
              </div>
            ),
        )}

        {!anyOptionSelected && submitted && (
          <p className="mt-1 font-xs text-red-600 font-medium">{errorMessage}</p>
        )}
      </div>
    );
  };

  const showBilling =
    (showProviderBillingStep && visit?.visitType === VisitType.HealthPlan) ||
    alwaysShowBillingStepForValidation ||
    user.isPsychiatrist;
  const isLateCancelOrNoShowSession =
    state === AttendanceOptionValue.LateCancellation || state === AttendanceOptionValue.NoShow;
  const selectedModalityOption = modalityOptions.find((option) => option.value === modalityType);
  const selectedAttendanceOption = attendanceOptions.find((option) => option.value === state);
  const selectedSessionTypeOption = sessionTypeOptions.find(
    (option) => option.value === isCoupleSession,
  );

  const isFormInvalid = useMemo(() => {
    const missingCommonRequirements =
      !sessionDateTime || !selectedAttendanceOption || !selectedModalityOption || !coachAssignment;

    if (invoicingDashboard && !showBilling && isProviderInvoicingOnly) {
      return (
        missingCommonRequirements ||
        !selectedSessionTypeOption ||
        (isGroupPractice && groupPracticeProviderUuid.length === 0)
      );
    }

    const missingBillingRequirements = showBilling ? !(cptCode && primaryDiagnosisCode) : false;

    return (
      missingCommonRequirements ||
      (shouldRequireReportedVisit && !visit) ||
      (missingBillingRequirements && !isLateCancelOrNoShowSession)
    );
  }, [
    coachAssignment,
    cptCode,
    groupPracticeProviderUuid,
    invoicingDashboard,
    isGroupPractice,
    isProviderInvoicingOnly,
    primaryDiagnosisCode,
    selectedAttendanceOption,
    selectedModalityOption,
    selectedSessionTypeOption,
    sessionDateTime,
    shouldRequireReportedVisit,
    showBilling,
    visit,
  ]);

  const isSubmitButtonDisabled = useMemo(
    () => submitted || isFormInvalid,
    [isFormInvalid, submitted],
  );

  useEffect(() => {
    if (!showBilling) {
      return;
    }

    const memberUuid = coachAssignment?.member.uuid;
    if (!memberUuid || !sessionDateTime?.toDate()) {
      return;
    }
    coachingVisitClaimable({
      variables: {
        memberUuid: memberUuid,
        state,
        scheduledTime: sessionDateTime?.toDate(),
      },
    });
  }, [coachAssignment, coachingVisitClaimable, sessionDateTime, showBilling, state]);

  useEffect(() => {
    const memberUuid = specificCoachAssignment?.member.uuid;
    if (memberUuid) {
      handleClientSelect(memberUuid);
    }
  }, [handleClientSelect, specificCoachAssignment]);

  const handleReportedCoachingVisitsCompleteFetch = (
    coachingVisits: CoachingVisitToReportQuery,
  ) => {
    if (coachingVisits?.coachingVisitToReport?.length === 0 && specificCoachAssignment?.member) {
      const { firstName, lastName } = specificCoachAssignment.member;
      enqueueSnackbar(
        `There are currently no valid sessions to report for ${firstName} ${lastName}.`,
        { variant: 'error' },
      );

      if (onCancel) {
        onCancel();
      }
    }
  };

  const handleSessionDateChange = (date: Moment | null) => {
    if (!date) {
      return;
    }

    const newDate = moment.tz(date.toISOString(), user.localTimezone);
    setSessionDateTime(newDate);

    if (onSessionDateChange) {
      onSessionDateChange(newDate);
    }
  };

  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <div className={`${classes.dialogContentInner} flex-grow`}>
          {/* show loading only if there is an existing member */}
          {specificCoachAssignment?.member && getCoachAssignmentLoading ? (
            <CircularProgress size={100} />
          ) : (
            <>
              <div className={classes.dialogContentLeft}>
                <div className={classes.visitRow}>
                  <span className="font-extrabold text-gray-900 text-xl">
                    <span className="text-blue-400">Step 1:</span> Session information
                  </span>
                </div>

                {isGroupPractice && (
                  <div data-testid={InvoicingFormTestID.ProviderField} className={classes.visitRow}>
                    <label className="text-sm font-black text-gray-800 leading-loose">
                      <span className="text-red-500">*</span> Provider
                    </label>
                    <ProviderSearchSelection
                      memberClient={memberClient}
                      onProviderSelect={handleProviderSelect}
                      canShowError={!!submitted}
                    />
                  </div>
                )}

                <div data-testid={InvoicingFormTestID.ClientField} className={classes.visitRow}>
                  <label className="text-sm font-black text-gray-800 leading-loose">
                    <span className="text-red-500">*</span> Client
                  </label>

                  {specificCoachAssignment?.member && (
                    <div className="inline-flex text-sm w-full p-2 text-gray-800 rounded-md border bg-gray-300 border-gray-300">
                      {`${specificCoachAssignment.member.firstName} ${specificCoachAssignment.member.lastName}`}
                    </div>
                  )}

                  {!specificCoachAssignment?.member && (
                    <>
                      {isGroupPractice && (
                        <GroupPracticeClientSearchSelection
                          providerUuid={groupPracticeProviderUuid}
                          memberClient={memberClient}
                          onClientSelect={handleClientSelect}
                          canShowError={!!submitted}
                        />
                      )}

                      {!isGroupPractice && (
                        <ClientSearchSelection
                          memberClient={memberClient}
                          onClientSelect={handleClientSelect}
                          canShowError={!!submitted}
                        />
                      )}

                      {visitsRemaining && (
                        <div data-testid={InvoicingFormTestID.ClientSessionsInfo} className="mt-4">
                          <div
                            className={classNames('w-full p-2 rounded-md border', {
                              'text-gray-700 bg-gray-200 border-gray-200':
                                visitsRemaining.visitsRemaining >= 1,
                              'text-orange-500 bg-orange-200 border-orange-200':
                                visitsRemaining.visitsRemaining < 1,
                            })}
                          >
                            <div className="mb-2">
                              <div className="flex items-center">
                                <span className="font-extrabold">Sessions left</span>
                                <QuestionBubble
                                  warning={visitsRemaining.visitsRemaining < 1}
                                  popoverTitle="Sessions left"
                                  sections={[
                                    {
                                      sectionBody: (
                                        <div className="leading-6">
                                          The number of available sessions. If a user runs out of
                                          sessions, please reach out to{' '}
                                          <a
                                            className="text-blue-200 font-semibold pointer-events-auto"
                                            href="mailto:provider@joinmodernhealth.com"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            provider@joinmodernhealth.com
                                          </a>
                                          .
                                        </div>
                                      ),
                                    },
                                  ]}
                                />
                              </div>
                              <span>{`${visitsRemaining.visitsRemaining} sessions left (out of ${visitsRemaining.coveredVisitsTotal})`}</span>
                            </div>
                            <div>
                              <div className="flex items-center">
                                <span className="font-extrabold">Reset date</span>
                                <QuestionBubble
                                  warning={visitsRemaining.visitsRemaining < 1}
                                  popoverTitle="Reset date"
                                  sections={[
                                    {
                                      sectionBody: (
                                        <div className="leading-6">
                                          This is the date when the member's session count will
                                          reset back to the full original amount.
                                        </div>
                                      ),
                                    },
                                  ]}
                                />
                              </div>
                              <span>{moment(visitsRemaining.resetDate).format('MM/DD/YYYY')}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className={classes.visitRow}>
                  <Typography className={classes.formLabel}>
                    <span className={classes.formLabelStar}>* </span>Session Date and Time
                  </Typography>

                  {shouldRequireReportedVisit && (
                    <ReportedCoachingVisitSelector
                      disabled={!canSelectReportedCoachingVisits}
                      memberClient={memberClient}
                      memberUuid={coachAssignment?.member.uuid}
                      onChange={setVisit}
                      onCompleted={handleReportedCoachingVisitsCompleteFetch}
                      selected={visit as ReportedCoachingVisit}
                      user={user}
                    />
                  )}

                  {!shouldRequireReportedVisit && (
                    <MHDatePicker
                      dataTestid={InvoicingFormTestID.MHDatePicker}
                      format="DD-MMM-YY, hh:mm a, z"
                      onChange={handleSessionDateChange}
                      placeholder="Select session date and time"
                      Picker={KeyboardDateTimePicker}
                      showError={submitted && !sessionDateTime}
                      value={sessionDateTime}
                    />
                  )}
                </div>

                <div className={classes.visitRow}>
                  <div className="flex items-center">
                    <Typography className={classes.formLabel}>
                      <span className={classes.formLabelStar}>* </span>Attendance
                    </Typography>
                    <QuestionBubble
                      popoverPosition={inModal ? PopoverPosition.Right : PopoverPosition.Bottom}
                      popoverTitle="Attendance"
                      sections={[
                        {
                          sectionTitle: 'Attended',
                          sectionBody: 'The client attended the session during this time',
                        },
                        {
                          sectionTitle: 'Late Cancellation',
                          sectionBody:
                            'The client cancelled the session 48 hours before the session scheduled time',
                        },
                        {
                          sectionTitle: 'No Show',
                          sectionBody: 'The client did not show up to the session',
                        },
                      ]}
                    />
                  </div>

                  {renderRadioButtons(
                    attendanceOptions,
                    state,
                    setState,
                    'Select client’s attendance',
                  )}
                </div>

                <div className={classes.visitRow}>
                  <Typography className={classes.formLabel}>
                    <span className={classes.formLabelStar}>* </span>Modality
                  </Typography>

                  {renderRadioButtons(
                    modalityOptions,
                    modalityType,
                    setModalityType,
                    'Select session modality',
                  )}
                </div>

                {isTherapy && (
                  <div className={classes.visitRow}>
                    <div className="flex items-center">
                      <Typography className={classes.formLabel}>
                        <span className={classes.formLabelStar}>* </span>Session Type
                      </Typography>
                      <QuestionBubble
                        popoverPosition={inModal ? PopoverPosition.Right : PopoverPosition.Bottom}
                        popoverTitle="Session type"
                        sections={[
                          {
                            sectionTitle: SessionTypeOptionLabel.IndividualSession,
                            sectionBody: 'This session occurred with 1 client',
                          },
                          {
                            sectionTitle: SessionTypeOptionLabel.CouplesSession,
                            sectionBody: 'This session occurred with 2 or more clients',
                          },
                        ]}
                      />
                    </div>

                    {renderRadioButtons(
                      sessionTypeOptions,
                      isCoupleSession,
                      setIsCoupleSession,
                      'Select session type',
                    )}
                  </div>
                )}
              </div>

              {showBilling && coachAssignment && (
                <div className={classes.dialogContentLeft}>
                  <BillingCodesForm
                    companyUuid={coachAssignment.member.company.uuid}
                    serviceType={serviceType}
                    onSelectCptCode={handleCptCodeSelected}
                    onSelectPrimaryDiagnosisCode={handlePrimaryDiagnosisCodeSelected}
                    onSelectSecondaryDiagnosisCodes={handleSecondaryDiagnosisCodesSelected}
                    disabled={
                      (coachingVisitClaimableLoading || !isCoachingVisitClaimable) &&
                      !user.isPsychiatrist
                    }
                  />
                </div>
              )}

              <div className={classes.dialogContentRight}>
                <RateSessionForm
                  organizationImprovements={organizationImprovements}
                  setOrganizationImprovements={setOrganizationImprovements}
                  abilityToHelpClient={abilityToHelpClient}
                  setAbilityToHelpClient={setAbilityToHelpClient}
                  workedOnAgreedUponGoals={workedOnAgreedUponGoals}
                  setWorkedOnAgreedUponGoals={setWorkedOnAgreedUponGoals}
                  clearPlanToWorkTowardGoals={clearPlanToWorkTowardGoals}
                  setClearPlanToWorkTowardGoals={setClearPlanToWorkTowardGoals}
                  showBilling={showBilling}
                  disabled={isLateCancelOrNoShowSession}
                />
              </div>
            </>
          )}
        </div>

        <div className={classes.buttonsContainer}>
          <div className={classes.buttonsWrapper}>
            {!isProviderInvoicingOnly && (
              <button
                className="flex-grow bg-white text-blue-400 p-1 px-4 text-sm font-extrabold border border-gray-400 rounded"
                onClick={onCancel}
              >
                Cancel
              </button>
            )}

            <MHButton
              fullWidth
              onClick={handleSubmit}
              buttonWrapperExtraClasses={[classes.submitButtonSize]}
              disabled={isSubmitButtonDisabled}
              loading={saving}
            >
              Submit
            </MHButton>
          </div>
        </div>
      </DialogContent>

      <InvoiceSubmissionSummaryModal
        onClose={() => setInvoiceSubmissionSummaryModalVisible(false)}
        onSubmit={handleSubmit}
        visible={invoiceSubmissionSummaryModalVisible}
        showSessionType={!!isTherapy}
        name={`${coachAssignment?.member?.firstName || ''} ${
          coachAssignment?.member?.lastName || ''
        }`}
        company={coachAssignment?.member?.company?.name || ''}
        email={coachAssignment?.member?.email || ''}
        dateAndTime={sessionDateTime ? sessionDateTime.format('DD-MMM-YY, hh:mm a, z') : ''}
        modality={ModalityOptionLabel[selectedModalityOption?.label || '']}
        attendance={AttendanceOptionLabel[selectedAttendanceOption?.label || '']}
        sessionType={SessionTypeOptionLabel[selectedSessionTypeOption?.label || '']}
        saving={saving}
      />
    </>
  );
};

export default withStyles(styles)(InvoicingForm);
